import { db, kvGet, kvSet, SYNC_TABLES } from './db';
import { supabase } from './supabase';
import { writable } from './schema';
import type { OutboxEntry } from '@/types/models';
import { useSyncStore, type SyncIssue } from '@/stores/syncStore';
import { toast } from '@/stores/toastStore';
import { humanise } from './format';
import { ENTITY_TABLE, restoreFromServer, unkeptFields, unkeptMessage } from './reconcile';

const WATERMARK_KEY = 'sync.watermark';
const EPOCH_KEY = 'sync.data_epoch';
const MAX_ATTEMPTS = 8;
const POLL_MS = 60_000;

interface PullResponse {
  server_time: string;
  since: string;
  data: Record<string, Array<Record<string, unknown>>>;
}

/* ------------------------------------------------------------------ */
/* Pull                                                                */
/* ------------------------------------------------------------------ */

/**
 * Everything changed since our watermark, in one round trip. The next
 * watermark is the SERVER's clock, not the device's - tablets that have
 * been switched off for a week have unreliable clocks.
 */
export async function pullChanges(): Promise<number> {
  // A rebuild (scope change, epoch) while this pull is on the wire empties
  // the copy and resets the watermark. This pull's rows and watermark are
  // then stale: writing them would set the watermark to "now" over an empty
  // copy, and nothing would ever fill it again. Discard and pull afresh.
  const generation = mirrorGeneration;
  const since = (await kvGet<string>(WATERMARK_KEY)) ?? '-infinity';
  const { data, error } = await supabase.rpc('pull_changes', { p_since: since });
  if (error) throw error;
  if (generation !== mirrorGeneration) return pullChanges();

  const payload = data as PullResponse;

  // Sync carries changes and soft deletes. A bulk load or clear of demo
  // data hard-deletes thousands of rows, which no pull would ever report,
  // so the server bumps settings.data_epoch instead. A device that sees
  // the epoch move rebuilds its copy from scratch — keeping the outbox,
  // captured photographs and its own bookkeeping, so nothing it has not
  // sent yet is lost.
  const epochRow = payload.data.settings?.find((r) => r.key === 'data_epoch');
  if (epochRow) {
    const epoch = String(epochRow.value);
    const known = await kvGet<string>(EPOCH_KEY);
    await kvSet(EPOCH_KEY, epoch);
    if (known !== undefined && known !== epoch && since !== '-infinity') {
      await rebuildMirror();
      return pullChanges();
    }
  }

  let rowCount = 0;
  if (generation !== mirrorGeneration) return pullChanges();

  await db.transaction('rw', SYNC_TABLES.map((t) => db.table(t)), async () => {
    for (const table of SYNC_TABLES) {
      const rows = payload.data[table];
      if (!rows?.length) continue;

      const live = rows.filter((r) => !r.deleted_at);
      const dead = rows.filter((r) => r.deleted_at);
      const keyName = table === 'settings' ? 'key' : 'id';

      if (live.length) await db.table(table).bulkPut(live);
      if (dead.length) {
        await db.table(table).bulkDelete(dead.map((r) => r[keyName] as string));
      }
      rowCount += rows.length;
    }
  });

  if (generation !== mirrorGeneration) return pullChanges();
  await kvSet(WATERMARK_KEY, payload.server_time);
  return rowCount;
}

/**
 * Empty every mirrored table and start the watermark again. The outbox,
 * the photographs waiting to upload and kv are client-only and survive;
 * anything queued is pushed on the next sync as usual.
 */
/** Bumped by every rebuild; a pull that started before it is discarded. */
let mirrorGeneration = 0;

async function rebuildMirror(): Promise<void> {
  mirrorGeneration += 1;
  await db.transaction('rw', SYNC_TABLES.map((t) => db.table(t)), async () => {
    for (const table of SYNC_TABLES) await db.table(table).clear();
  });
  await kvSet(WATERMARK_KEY, '-infinity');
}

const SCOPE_KEY = 'sync.scope';
const MIRROR_VERSION = 'v2';

/**
 * What this device's copy was pulled for: the quarry, or one branch. Sync
 * brings only rows changed since the last pull, so when an administrator
 * moves a person to a branch (or between branches) the rows they may now
 * see are old ones that no pull would ever bring, and rows they may no
 * longer see would linger. A change of scope rebuilds the copy.
 */
export async function ensureScope(
  homeSite: string | null | undefined,
  role?: string | null,
  access?: { sites: string[]; modules: string[] } | null,
): Promise<void> {
  const costs = !homeSite && ['maintenance', 'manager', 'admin'].includes(role ?? '');
  // 0055: the sites and modules the server lets this user read are part of
  // the scope too — a change means rows to fetch or to drop.
  const extra = access
    ? `|${[...access.sites].sort().join(',')}|${[...access.modules].sort().join(',')}`
    : '';
  // MIRROR_VERSION: bumped to rebuild every device once — v2 repairs the
  // copies emptied by the pull/rebuild race fixed with mirrorGeneration.
  const scope = `${MIRROR_VERSION}|` + (homeSite ?? 'quarry') + (costs ? '+workshop' : '') + extra;
  // 0067 expands readable references without changing a user's module
  // choices. Rewind the pull once, but do NOT clear offline diaries or any
  // other local rows just to retrieve the newly visible shifts/categories.
  if (!(await kvGet<boolean>('sync.references.0067'))) {
    mirrorGeneration += 1; // an earlier in-flight pull must not restore the old watermark
    await kvSet(WATERMARK_KEY, '-infinity');
    await kvSet('sync.references.0067', true);
  }
  const known = await kvGet<string>(SCOPE_KEY);
  await kvSet(SCOPE_KEY, scope);
  if (known !== undefined && known !== scope) {
    await rebuildMirror();
    void syncNow();
  }
}

/** Force a full re-pull, e.g. after a schema change or a corrupt cache. */
export async function resetWatermark(): Promise<void> {
  await kvSet(WATERMARK_KEY, '-infinity');
}

/* ------------------------------------------------------------------ */
/* Push                                                                */
/* ------------------------------------------------------------------ */

export async function queue(
  entry: Omit<OutboxEntry, 'seq' | 'created_at' | 'attempts' | 'next_attempt_at' | 'last_error'>,
): Promise<void> {
  await db.outbox.add({
    ...entry,
    created_at: new Date().toISOString(),
    attempts: 0,
    next_attempt_at: new Date().toISOString(),
    last_error: null,
  });
  useSyncStore.getState().setPending(await db.outbox.count());
  void syncNow();
}

// Outbox entity -> table lives in reconcile.ts (ENTITY_TABLE), shared with
// restoreFromServer so the two cannot drift.

/**
 * Evidence goes up in two steps, and the order is the whole point.
 *
 * The bytes go to the bucket first, the row second. Do it the other way and
 * a dropped connection leaves a photograph in the register that nobody can
 * open. This way the worst case is an orphaned object in the bucket, which
 * costs a few hundred kilobytes and misleads no one.
 *
 * `upsert: true` is what makes a replay safe: the path is the attachment's
 * own id, so re-uploading overwrites its own object instead of creating a
 * second copy.
 */
async function pushAttachment(entry: OutboxEntry): Promise<void> {
  const row = entry.payload as {
    id: string; storage_path: string; mime_type: string;
  };

  const pending = await db.evidence_blobs.get(row.id);
  if (pending) {
    const { error: upErr } = await supabase.storage
      .from('evidence')
      .upload(pending.storage_path, pending.blob, {
        contentType: pending.mime,
        upsert: true,
      });
    if (upErr) throw upErr;
  }
  // No local blob means the bytes are already up and only the row failed —
  // the retry finishes the job rather than starting over.

  const { error } = await supabase.from('attachments').upsert(row as never);
  if (error) throw error;

  // The bucket holds it now. Keeping a second copy would fill a tablet that
  // photographs a dozen failures a week.
  await db.evidence_blobs.delete(row.id);
}

async function pushEntry(entry: OutboxEntry): Promise<void> {
  if (entry.entity === 'diary') {
    // Header, allocations and activities in one transaction, so a
    // partially synced diary can never exist.
    const { error } = await supabase.rpc('upsert_diary', { p_payload: entry.payload });
    if (error) throw error;
    const p = entry.payload as { diary?: Record<string, unknown> };
    await reconcile(entry, 'daily_diary', p.diary);
    return;
  }

  if (entry.entity === 'vehicle_check' && entry.op === 'upsert') {
    // Header and items in one transaction (0036), as the diary.
    const { error } = await supabase.rpc('upsert_vehicle_check', { p_payload: entry.payload });
    if (error) throw error;
    const p = entry.payload as { check?: Record<string, unknown> };
    await reconcile(entry, 'vehicle_checks', p.check);
    return;
  }

  if (entry.entity === 'attachment' && entry.op === 'upsert') {
    await pushAttachment(entry);
    return;
  }

  const table = ENTITY_TABLE[entry.entity];
  if (!table) throw new Error(`No table mapped for outbox entity "${entry.entity}"`);

  if (entry.op === 'delete') {
    const { error } = await supabase
      .from(table)
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', entry.entity_id);
    if (error) throw error;
    return;
  }

  const sent = writable(table, entry.payload as Record<string, unknown>);
  const { data, error } = await supabase
    .from(table)
    .upsert(sent as never)
    .select()
    .maybeSingle();
  if (error) throw error;
  await reconcile(entry, table, sent, data as Record<string, unknown> | null);
}

/**
 * After a push the server's row is the truth: put it on the device, and
 * say so if the server did not keep a field that was sent. Skipped when a
 * later change to the same record is already queued — that one wins on the
 * device and will be pushed next.
 */
async function reconcile(
  entry: OutboxEntry,
  table: string,
  sent: Record<string, unknown> | undefined,
  row?: Record<string, unknown> | null,
): Promise<void> {
  try {
    const newer = await db.outbox
      .where('entity_id').equals(entry.entity_id)
      .filter((e) => (e.seq ?? 0) > (entry.seq ?? 0))
      .count();
    if (newer > 0) return;

    let saved = row;
    if (saved === undefined) {
      const res = await supabase.from(table).select('*').eq('id', entry.entity_id).maybeSingle();
      if (res.error) return;
      saved = res.data as Record<string, unknown> | null;
    }
    if (!saved) return;   // written but not readable to this role: leave the device copy

    // Merged over the device copy, not swapped for it: the server's values
    // win, and anything the answer leaves out stays as it was.
    const local = (await db.table(table).get(entry.entity_id)) as Record<string, unknown> | undefined;
    await db.table(table).put({ ...(local ?? {}), ...saved });

    const lost = unkeptFields(table, sent, saved);
    if (lost.length) toast.error(unkeptMessage(lost, `the ${humanise(entry.entity).toLowerCase()}`));
  } catch {
    // Reconciling is a courtesy; the push itself has succeeded.
  }
}

/** Permanent failures must not be retried forever - a human has to look. */
function isPermanent(err: unknown): boolean {
  const code = (err as { code?: string })?.code ?? '';
  // 23xxx = integrity violation, 42xxx = permission/syntax, P0001 = raised by our triggers
  return /^(23|42)/.test(code) || code === 'P0001';
}

export async function drainOutbox(): Promise<{ sent: number; failed: number }> {
  const now = new Date().toISOString();
  const entries = await db.outbox.orderBy('seq').filter((e) => e.next_attempt_at <= now).toArray();

  let sent = 0;
  let failed = 0;

  for (const entry of entries) {
    try {
      await pushEntry(entry);
      await db.outbox.delete(entry.seq!);
      sent++;
    } catch (err) {
      failed++;
      const attempts = entry.attempts + 1;
      const message = (err as Error)?.message ?? String(err);

      if (isPermanent(err) || attempts >= MAX_ATTEMPTS) {
        // Park it in Sync Issues rather than looping - the supervisor decides.
        await db.outbox.update(entry.seq!, {
          attempts,
          last_error: message,
          next_attempt_at: '9999-12-31T00:00:00.000Z',
        });
        useSyncStore.getState().addIssue({
          seq: entry.seq!,
          entity: entry.entity,
          entity_id: entry.entity_id,
          error: message,
        });
        // Said at once, on whatever screen the person is on. The save
        // itself reported success minutes ago, offline-first.
        toast.error(
          `${humanise(entry.entity)} was not saved on the server: ${message} `
          + 'Open Sync issues (the red status at the top) to retry or discard it.',
        );
      } else {
        const backoff = Math.min(2 ** attempts * 1000, 5 * 60_000);
        await db.outbox.update(entry.seq!, {
          attempts,
          last_error: message,
          next_attempt_at: new Date(Date.now() + backoff).toISOString(),
        });
      }
      break; // preserve ordering - stop at the first failure
    }
  }

  useSyncStore.getState().setPending(await db.outbox.count());
  return { sent, failed };
}

/* ------------------------------------------------------------------ */
/* Sync issues                                                         */
/* ------------------------------------------------------------------ */

const PARKED = '9000-01-01';

/** Rebuild the issue list from the outbox. The store is memory only; the
 *  parked entries are not, so after a reload the red status must come
 *  back rather than read "Synced" over changes the server refused. */
export async function refreshIssues(): Promise<void> {
  const parked = await db.outbox.filter((e) => e.next_attempt_at > PARKED).toArray();
  const issues: SyncIssue[] = parked.map((e) => ({
    seq: e.seq!, entity: e.entity, entity_id: e.entity_id,
    error: e.last_error ?? 'Rejected by the server',
  }));
  useSyncStore.getState().setIssues(issues);
}

export async function retryIssue(seq: number): Promise<void> {
  await db.outbox.update(seq, { next_attempt_at: new Date().toISOString(), attempts: 0 });
  useSyncStore.getState().clearIssue(seq);
  await syncNow();
}

/**
 * Drop a refused change and put the server's version back on the device.
 * Only deleting the queue entry (as before) left the refused value on
 * screen indefinitely: the server row had not changed, so no pull would
 * ever bring the right one back.
 */
export async function discardIssue(seq: number): Promise<{ restored: boolean }> {
  const entry = await db.outbox.get(seq);
  await db.outbox.delete(seq);
  useSyncStore.getState().clearIssue(seq);
  useSyncStore.getState().setPending(await db.outbox.count());
  if (!entry) return { restored: true };

  // Another queued change to the same record still stands; leave the
  // device copy as it is.
  const others = await db.outbox.where('entity_id').equals(entry.entity_id).count();
  if (others > 0) return { restored: true };

  if (entry.entity === 'attachment') {
    await db.evidence_blobs.delete(entry.entity_id);
  }
  const restored = navigator.onLine ? await restoreFromServer(entry.entity, entry.entity_id) : false;
  return { restored };
}

/* ------------------------------------------------------------------ */
/* Orchestration                                                       */
/* ------------------------------------------------------------------ */

let running = false;
/** Asked for while a sync was running: something was queued mid-run (a
 *  breakdown and then the check that raised it), so run once more. */
let again = false;

export async function syncNow(): Promise<void> {
  if (running) { again = true; return; }
  if (!navigator.onLine) return;
  const { data } = await supabase.auth.getSession();
  if (!data.session) return;

  running = true;
  const store = useSyncStore.getState();
  store.setSyncing(true);
  try {
    await drainOutbox();
    const rows = await pullChanges();
    store.setLastSync(new Date().toISOString(), rows);
    store.setError(null);
  } catch (err) {
    store.setError((err as Error)?.message ?? 'Sync failed');
  } finally {
    running = false;
    store.setSyncing(false);
    if (again) {
      again = false;
      void syncNow();
    }
  }
}

let timer: number | undefined;

export function startSync(): () => void {
  const online = () => {
    useSyncStore.getState().setOnline(true);
    void syncNow();
  };
  const offline = () => useSyncStore.getState().setOnline(false);
  const focus = () => void syncNow();

  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  window.addEventListener('focus', focus);
  timer = window.setInterval(() => void syncNow(), POLL_MS);

  useSyncStore.getState().setOnline(navigator.onLine);
  void refreshIssues();
  void syncNow();

  return () => {
    window.removeEventListener('online', online);
    window.removeEventListener('offline', offline);
    window.removeEventListener('focus', focus);
    if (timer) window.clearInterval(timer);
  };
}
